import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import LocationBanner from '../components/LocationBanner';
import { FaUsers, FaCheckCircle, FaCalendarCheck, FaTicketAlt, FaStar, FaMoneyBillWave, FaDownload, FaLock } from 'react-icons/fa';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import '../styles/AnalyticsPage.css';

const AnalyticsPage = () => {
  const { business } = useAuth();
  const { locationId } = useLocation();
  const currentTier = business?.premium_tier || 1;
  const [period, setPeriod] = useState('month'); // week, month, year, all
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCheckIns: 0,
    totalBookings: 0,
    totalCouponsUsed: 0,
    averageRating: 0,
    estimatedRevenue: 0
  });
  const [chartData, setChartData] = useState({
    daily: [],
    couponTypes: [],
    hourlyDistribution: []
  });
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [competitors, setCompetitors] = useState([]);

  useEffect(() => {
    loadAnalytics();
    if (currentTier >= 3) loadCompetitors();
  }, [business, period, locationId]);

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();

    switch (period) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'all':
        startDate = new Date('2024-01-01');
        break;
      default:
        startDate.setMonth(now.getMonth() - 1);
    }

    return { startDate: startDate.toISOString(), endDate: now.toISOString() };
  };

  const TIER_POINTS = { '5-20': 10, '20-50': 35, '50+': 80 };
  const TIER_MIN_AZN = { '5-20': 5, '20-50': 20, '50+': 50 };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();

      // 1. QR Scan logs (point_logs) — real ziyarət datası
      const { data: scanLogs } = await supabase
        .from('point_logs')
        .select('*')
        .eq('business_id', business.id)
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: true });

      const scans = scanLogs || [];

      // 2. Bookings (accepted)
      const { data: bookings } = await supabase
        .from('flash_bookings')
        .select('*')
        .eq('business_id', business.id)
        .eq('status', 'accepted')
        .gte('created_at', startDate)
        .lte('created_at', endDate);

      // 3. Coupon logs
      const { data: allCouponLogs } = await supabase
        .rpc('get_coupon_logs', { p_business_id: business.id });
      const couponsUsed = (allCouponLogs || []).filter(c =>
        c.created_at >= startDate && c.created_at <= endDate
      );

      // 4. Unique users (scan-lardan)
      const uniqueUsers = new Set(scans.map(s => s.user_id).filter(Boolean));

      // 5. Top users (ən çox scan edilən)
      const userScanCount = {};
      scans.forEach(s => {
        if (!s.user_id) return;
        if (!userScanCount[s.user_id]) {
          userScanCount[s.user_id] = { user_id: s.user_id, full_name: s.user_name || 'İstifadəçi', count: 0 };
        }
        userScanCount[s.user_id].count++;
      });
      const topUsersList = Object.values(userScanCount)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 6. Günlük scan qrafiki
      const dailyData = {};
      scans.forEach(s => {
        const date = new Date(s.created_at).toLocaleDateString('az-AZ');
        dailyData[date] = (dailyData[date] || 0) + 1;
      });
      const dailyChartData = Object.entries(dailyData).map(([date, count]) => ({ date, count }));

      // 7. Kupon növləri qrafiki
      const couponTypeCount = {};
      couponsUsed.forEach(c => {
        const type = c.coupon_title || 'Digər';
        couponTypeCount[type] = (couponTypeCount[type] || 0) + 1;
      });
      const couponTypesData = Object.entries(couponTypeCount).map(([name, value]) => ({ name, value }));

      // 8. Saatlara görə ziyarət
      const hourlyData = {};
      scans.forEach(s => {
        const hour = new Date(s.created_at).getHours();
        hourlyData[hour] = (hourlyData[hour] || 0) + 1;
      });
      const hourlyChartData = Object.entries(hourlyData)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([hour, count]) => ({ hour: `${hour}:00`, count }));

      // 9. Real rating
      const { data: reviewsData } = await supabase
        .from('reviews').select('rating').eq('business_id', business.id);
      const avgRating = reviewsData?.length
        ? (reviewsData.reduce((s, r) => s + r.rating, 0) / reviewsData.length).toFixed(1)
        : 0;

      // 10. Təxmini gəlir — scan tier-lərindən min ödəniş
      const estimatedRevenue = scans.reduce((sum, s) => {
        return sum + (TIER_MIN_AZN[s.amount_tier] || 0);
      }, 0);

      setStats({
        totalUsers: uniqueUsers.size,
        totalCheckIns: scans.length,
        totalBookings: bookings?.length || 0,
        totalCouponsUsed: couponsUsed.length,
        averageRating: avgRating,
        estimatedRevenue,
      });

      setChartData({
        daily: dailyChartData,
        couponTypes: couponTypesData,
        hourlyDistribution: hourlyChartData,
      });

      setTopUsers(topUsersList);

    } catch (error) {
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Göstərici', 'Dəyər'],
      ['Unikal İstifadəçi', stats.totalUsers],
      ['Check-in Sayı', stats.totalCheckIns],
      ['Qəbul Edilən Rezervasiya', stats.totalBookings],
      ['İstifadə Edilən Kupon', stats.totalCouponsUsed],
      ['Orta Reytinq', stats.averageRating],
      ['Təxmini Gəlir (₼)', stats.estimatedRevenue],
      [],
      ['Tarix', 'Check-in'],
      ...chartData.daily.map(d => [d.date, d.count]),
      [],
      ['Saat', 'Ziyarət'],
      ...chartData.hourlyDistribution.map(d => [d.hour, d.count]),
      [],
      ['Ən Aktiv Müştərilər', 'Check-in Sayı'],
      ...topUsers.map(u => [u.full_name, u.count]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hesabat_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadCompetitors = async () => {
    if (!business?.city) return;
    try {
      const { data } = await supabase
        .from('businesses')
        .select('id, name, rating, premium_tier')
        .eq('city', business.city)
        .neq('id', business.id)
        .order('rating', { ascending: false })
        .limit(5);
      setCompetitors(data || []);
    } catch (e) {
      console.error('Competitor load error:', e);
    }
  };

  const getCouponTitle = (type) => {
    const titles = {
      'free_latte': 'Pulsuz Latte',
      'free_burger': 'Pulsuz Burger',
      'free_dessert': 'Pulsuz Desert',
      'free_drink': 'Pulsuz İçki',
      'discount_10': '10% Endirim',
      'discount_20': '20% Endirim'
    };
    return titles[type] || type;
  };

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

  if (loading) {
    return (
      <div className="analytics-loading">
        <h2>Yüklənir...</h2>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <LocationBanner />
      <div className="analytics-header">
        <div>
          <h1>📊 Hesabatlar və Audit</h1>
          <p>Cafegoo proqramının kafenizə gətirdiyi fayda</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {currentTier >= 3 ? (
            <button
              onClick={exportCSV}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#8B5CF6', color: '#fff', fontWeight: 600,
                fontSize: 13, cursor: 'pointer',
              }}
            >
              <FaDownload style={{ fontSize: 12 }} />
              CSV Export
            </button>
          ) : (
            <div
              title="Premium plana keçin"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                background: '#f5f5f5', color: '#aaa', fontWeight: 600,
                fontSize: 13, cursor: 'not-allowed', border: '1px solid #e0e0e0',
              }}
            >
              <FaLock style={{ fontSize: 11 }} />
              CSV Export
            </div>
          )}
        </div>
        <div className="period-selector">
          <button
            className={period === 'week' ? 'active' : ''}
            onClick={() => setPeriod('week')}
          >
            Son Həftə
          </button>
          <button
            className={period === 'month' ? 'active' : ''}
            onClick={() => setPeriod('month')}
          >
            Son Ay
          </button>
          <button
            className={period === 'year' ? 'active' : ''}
            onClick={() => setPeriod('year')}
          >
            Son İl
          </button>
          <button
            className={period === 'all' ? 'active' : ''}
            onClick={() => setPeriod('all')}
          >
            Bütün Vaxt
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="analytics-stats-grid">
        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#667eea' }}>
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>{stats.totalUsers}</h3>
            <p>Unikal İstifadəçi</p>
            <span className="stat-description">Proqramdan gələn müştərilər</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#27ae60' }}>
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.totalCheckIns}</h3>
            <p>Check-in Sayı</p>
            <span className="stat-description">Aktiv ziyarətlər</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#f39c12' }}>
            <FaCalendarCheck />
          </div>
          <div className="stat-content">
            <h3>{stats.totalBookings}</h3>
            <p>Qəbul Edilən Rezervasiya</p>
            <span className="stat-description">Flash booking-lər</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#e74c3c' }}>
            <FaTicketAlt />
          </div>
          <div className="stat-content">
            <h3>{stats.totalCouponsUsed}</h3>
            <p>İstifadə Edilən Kupon</p>
            <span className="stat-description">Kampanya təsiri</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#feca57' }}>
            <FaStar />
          </div>
          <div className="stat-content">
            <h3>{stats.averageRating}</h3>
            <p>Orta Reytinq</p>
            <span className="stat-description">Müştəri məmnuniyyəti</span>
          </div>
        </div>

        <div className="analytics-stat-card highlight">
          <div className="stat-icon" style={{ color: '#2ecc71' }}>
            <FaMoneyBillWave />
          </div>
          <div className="stat-content">
            <h3>{stats.estimatedRevenue}₼+</h3>
            <p>Proqramdan Gəlir</p>
            <span className="stat-description">Scan tierlərindən min. həcm</span>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* Daily Check-ins */}
        <div className="chart-card full-width">
          <h3>📈 Günlük Check-in Statistikası</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#667eea" strokeWidth={2} name="Check-in" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Hourly Distribution */}
        <div className="chart-card">
          <h3>🕐 Saatlara Görə Ziyarət</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.hourlyDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#764ba2" name="Ziyarət" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Coupon Types */}
        <div className="chart-card">
          <h3>🎟️ Kupon Növləri</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.couponTypes}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.couponTypes.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Users */}
      <div className="top-users-card">
        <h3>🏆 Ən Aktiv Müştərilər (Top 10)</h3>
        {topUsers.length === 0 ? (
          <p className="empty-state">Hələ məlumat yoxdur</p>
        ) : (
          <div className="top-users-list">
            {topUsers.map((user, index) => (
              <div key={user.user_id} className="top-user-item">
                <div className="user-rank">{index + 1}</div>
                {user.avatar_url ? (
                  <img src={av(user.avatar_url)} alt={user.full_name} className="user-avatar-small" />
                ) : (
                  <div className="user-avatar-placeholder-small">{user.full_name[0]}</div>
                )}
                <div className="user-name">{user.full_name}</div>
                <div className="user-count">{user.count} check-in</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rəqib Müqayisəsi — Premium only */}
      <div className="top-users-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>🏁 Rəqib Müqayisəsi — {business?.city}</h3>
          {currentTier < 3 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 700, color: '#8B5CF6',
              background: '#8B5CF618', border: '1px solid #8B5CF630',
              borderRadius: 6, padding: '3px 8px',
            }}>
              <FaLock style={{ fontSize: 9 }} /> Premium
            </span>
          )}
        </div>
        {currentTier < 3 ? (
          <div style={{
            textAlign: 'center', padding: '32px 20px',
            color: '#aaa', fontSize: 14,
            background: '#faf9ff', borderRadius: 12,
            border: '1px dashed #d0c0f0',
          }}>
            🔒 Rəqib müqayisəsi <strong style={{ color: '#8B5CF6' }}>Premium</strong> plana aiddir.<br />
            <span style={{ fontSize: 12 }}>Eyni şəhərdəki digər restoranlarla statistikanızı müqayisə edin.</span>
          </div>
        ) : competitors.length === 0 ? (
          <p className="empty-state">Eyni şəhərdə başqa biznes tapılmadı</p>
        ) : (
          <div>
            {/* Your stats row */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 10, marginBottom: 8,
              background: '#f0fdf4', border: '1px solid #86efac',
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, minWidth: 20 }}>★</div>
              <div style={{ flex: 1, fontWeight: 700, fontSize: 13 }}>{business?.name} (siz)</div>
              <div style={{ fontSize: 12, color: '#666' }}>⭐ {business?.rating || '—'}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{stats.totalCheckIns} check-in</div>
            </div>
            {competitors.map((c, i) => (
              <div key={c.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10, marginBottom: 6,
                background: '#f8f9fa', border: '1px solid #eee',
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, minWidth: 20, color: '#aaa' }}>{i + 1}</div>
                <div style={{ flex: 1, fontSize: 13 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#666' }}>⭐ {c.rating || '—'}</div>
                <div style={{ fontSize: 12, color: '#666' }}>⭐ {c.rating || '—'}</div>
                {c.premium_tier >= 3 && (
                  <span style={{ fontSize: 10, color: '#8B5CF6', fontWeight: 700 }}>Premium</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Insights */}
      <div className="insights-card">
        <h3>💡 Analiz və Tövsiyələr</h3>
        <div className="insights-list">
          <div className="insight-item success">
            <span className="insight-icon">✅</span>
            <p>
              Proqramdan <strong>{stats.totalUsers} unikal müştəri</strong> gəlib,{' '}
              cəmi <strong>{stats.totalCheckIns} scan</strong> edilib.
            </p>
          </div>

          {chartData.hourlyDistribution.length > 0 && (() => {
            const peak = [...chartData.hourlyDistribution].sort((a, b) => b.count - a.count)[0];
            return (
              <div className="insight-item info">
                <span className="insight-icon">📊</span>
                <p>
                  Ən sıx saat: <strong>{peak.hour}</strong> — bu saatda <strong>{peak.count} scan</strong> edilib.
                </p>
              </div>
            );
          })()}

          {stats.estimatedRevenue > 0 && (
            <div className="insight-item warning">
              <span className="insight-icon">💰</span>
              <p>
                Proqram vasitəsilə ən azı <strong>{stats.estimatedRevenue}₼</strong> dövriyyə yaranıb (scan tierlərindən hesablanıb).
              </p>
            </div>
          )}

          {stats.totalCouponsUsed > 0 && (
            <div className="insight-item success">
              <span className="insight-icon">🎟️</span>
              <p>
                Bu dövrdə <strong>{stats.totalCouponsUsed} kupon</strong> kafedə oxudulub.
                {chartData.couponTypes[0] && <> Ən populyar: <strong>{chartData.couponTypes.sort((a,b)=>b.value-a.value)[0]?.name}</strong>.</>}
              </p>
            </div>
          )}

          {stats.totalBookings > 0 && (
            <div className="insight-item info">
              <span className="insight-icon">📅</span>
              <p>
                <strong>{stats.totalBookings} rezervasiya</strong> qəbul edilib.
              </p>
            </div>
          )}

          {stats.averageRating > 0 && (
            <div className="insight-item">
              <span className="insight-icon">⭐</span>
              <p>
                Ortalama reytinq: <strong>{stats.averageRating}/5</strong>.{' '}
                {stats.averageRating >= 4.5
                  ? 'Əla nəticə — müştərilər çox razıdır!'
                  : stats.averageRating >= 4
                  ? 'Yaxşı nəticə — bir az daha yaxşılaşdırmaq mümkündür.'
                  : 'Müştəri şərhlərini izləyin və xidməti yaxşılaşdırın.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;